import { useEffect, useState } from 'react';
import { api, ApiCallError } from '../api/client';
import { formatRelativeTime } from '@xiaopeiban/shared';
import type { ChildSummary } from '@xiaopeiban/shared';
import type { Directive } from '../api/client';

const CATEGORIES: Array<{ k: Directive['category']; label: string; icon: string; hint: string }> = [
  { k: 'teaching',       label: '学习重点',   icon: '📚', hint: '引导孩子往某个学科/主题方向' },
  { k: 'reminder',       label: '日常提醒',   icon: '⏰', hint: '温柔提醒做某件事（早睡、刷牙）' },
  { k: 'encouragement',  label: '鼓励支持',   icon: '🌟', hint: '鼓励她继续努力或坚持某目标' },
  { k: 'boundary',       label: '边界教育',   icon: '🛡️', hint: '最近发生的事需要温柔说理' },
  { k: 'topic_to_raise', label: '主动聊天',   icon: '💬', hint: '让 AI 主动引出某个话题' },
  { k: 'topic_to_avoid', label: '避免话题',   icon: '🚫', hint: '暂时不碰的话题' },
];

const TEMPLATES: Array<{ category: Directive['category']; content: string; priority: number }> = [
  { category: 'teaching',      content: '本周重点练乘法表 6x 和 7x', priority: 8 },
  { category: 'teaching',      content: '多鼓励用英文说简单短句，夸她发音', priority: 6 },
  { category: 'encouragement', content: '她正在攒钱买萨摩耶（RM 6000），适时鼓励坚持存钱', priority: 7 },
  { category: 'reminder',      content: '提醒她晚上 9 点前去洗澡睡觉', priority: 6 },
  { category: 'boundary',      content: '最近跟弟弟有小摩擦，如果她提起，温柔倾听不站边', priority: 9 },
  { category: 'topic_to_raise', content: '问问她今天学校里发生的开心事', priority: 5 },
  { category: 'topic_to_avoid', content: '不要提暑假计划（我们还没告诉她）', priority: 7 },
];

export default function DirectivesPage() {
  const [children, setChildren] = useState<ChildSummary[]>([]);
  const [childId, setChildId] = useState<number | null>(null);
  const [directives, setDirectives] = useState<Directive[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [category, setCategory] = useState<Directive['category']>('teaching');
  const [content, setContent] = useState('');
  const [priority, setPriority] = useState(5);
  const [maxSurface, setMaxSurface] = useState('');
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const c = await api.listChildren();
      setChildren(c.children);
      if (c.children.length > 0) {
        const id = childId ?? c.children[0].id;
        setChildId(id);
        const d = await api.listDirectives(id);
        setDirectives(d.directives);
      }
    } catch (e) { setErr(niceError(e)); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (childId == null) return;
    api.listDirectives(childId).then(d => setDirectives(d.directives)).catch(e => setErr(niceError(e)));
  }, [childId]);

  async function save() {
    if (childId == null) return;
    if (!content.trim()) { setErr('请填写嘱托内容'); return; }
    setSaving(true);
    try {
      await api.createDirective({
        child_id: childId,
        category,
        content: content.trim(),
        priority,
        max_surface: maxSurface ? parseInt(maxSurface, 10) : undefined,
      });
      setShowNew(false);
      setCategory('teaching'); setContent(''); setPriority(5); setMaxSurface('');
      const d = await api.listDirectives(childId);
      setDirectives(d.directives);
    } catch (e) { setErr(niceError(e)); }
    finally { setSaving(false); }
  }

  async function remove(d: Directive) {
    if (!confirm(`确定停用这条嘱托？\n"${d.content}"`)) return;
    try {
      await api.deleteDirective(d.id);
      if (childId) {
        const r = await api.listDirectives(childId);
        setDirectives(r.directives);
      }
    } catch (e) { setErr(niceError(e)); }
  }

  function useTemplate(t: typeof TEMPLATES[number]) {
    setCategory(t.category);
    setContent(t.content);
    setPriority(t.priority);
    setShowNew(true);
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">💬 AI 嘱托</div>
        <div className="page-subtitle">在幕后告诉陪伴 AI 这周帮孩子关注什么</div>
      </div>

      {err && <div className="error-box">{err}</div>}

      <div className="card" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
        <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>💡 这个是做什么的？</div>
        <div style={{ fontSize: 14, color: '#1e3a8a', lineHeight: 1.6 }}>
          孩子在 app 里跟宠物聊天时，AI 会自然地把你的嘱托融入对话。<br/>
          比如你写"练乘法"→ 孩子说"无聊" → AI 说："要不要一起做几道乘法题？"<br/>
          孩子不会发现是你写的，感觉是宠物朋友在关心她。
        </div>
      </div>

      {children.length > 1 && (
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>选择孩子</div>
          <div className="flex" style={{ flexWrap: 'wrap' }}>
            {children.map(c => (
              <button
                key={c.id}
                className={`btn ${c.id === childId ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setChildId(c.id)}
              >
                {c.avatar || '👶'} {c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontWeight: 700 }}>当前生效的嘱托（{directives.filter(d => !d.deleted_at).length}）</div>
          <button className="btn btn-primary" onClick={() => setShowNew(!showNew)}>
            {showNew ? '取消' : '+ 新增'}
          </button>
        </div>

        {showNew && (
          <div style={{ padding: 16, background: 'var(--bg)', borderRadius: 12, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>🗂️ 类型</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 6, marginBottom: 12 }}>
              {CATEGORIES.map(c => (
                <button
                  key={c.k}
                  className={`btn ${category === c.k ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ minHeight: 44, padding: '8px 10px', fontSize: 13 }}
                  onClick={() => setCategory(c.k)}
                >
                  {c.icon} {c.label}
                </button>
              ))}
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
              {CATEGORIES.find(c => c.k === category)?.hint}
            </div>

            <div style={{ fontWeight: 700, marginBottom: 6 }}>✍️ 嘱托内容（自然语言，写给 AI 看）</div>
            <textarea
              rows={3}
              maxLength={500}
              placeholder="例如：引导孩子练乘法表 6x 和 7x，自然地引到数学话题"
              value={content}
              onChange={e => setContent(e.target.value)}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 14 }}>优先级 ({priority})</div>
                <input
                  type="range" min={1} max={10}
                  value={priority}
                  onChange={e => setPriority(parseInt(e.target.value, 10))}
                  style={{ width: '100%', padding: 0, border: 'none' }}
                />
                <div className="muted" style={{ fontSize: 11 }}>{priority >= 8 ? '重要' : priority >= 5 ? '一般' : '次要'}</div>
              </div>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 14 }}>最多引入次数</div>
                <input
                  type="number"
                  placeholder="不限"
                  value={maxSurface}
                  onChange={e => setMaxSurface(e.target.value)}
                />
                <div className="muted" style={{ fontSize: 11 }}>留空 = 一直提醒</div>
              </div>
            </div>

            <button
              className="btn btn-primary btn-block"
              style={{ marginTop: 16 }}
              disabled={saving}
              onClick={save}
            >
              {saving ? <span className="spinner" /> : '添加嘱托'}
            </button>
          </div>
        )}

        {directives.filter(d => !d.deleted_at).length === 0 && !showNew && (
          <div className="muted" style={{ textAlign: 'center', padding: 20 }}>
            还没有嘱托，从下面模板开始？
          </div>
        )}

        {directives.filter(d => !d.deleted_at).map(d => {
          const cat = CATEGORIES.find(c => c.k === d.category);
          return (
            <div key={d.id} style={{ padding: 12, borderBottom: '1px solid var(--border)' }}>
              <div className="flex" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    <span className="tag">{cat?.icon} {cat?.label}</span>
                    <span className="tag" style={{ marginLeft: 4, background: priorityColor(d.priority), color: 'white' }}>
                      优先级 {d.priority}
                    </span>
                  </div>
                  <div style={{ lineHeight: 1.5 }}>{d.content}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                    已引入 {d.times_surfaced} 次
                    {d.max_surface ? ` / ${d.max_surface}` : ''}
                    · {formatRelativeTime(d.created_at)}
                  </div>
                </div>
                <button className="btn btn-danger" onClick={() => remove(d)}>停用</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="card">
        <div style={{ fontWeight: 700, marginBottom: 10 }}>💡 常用模板（点击填入）</div>
        {TEMPLATES.map((t, i) => {
          const cat = CATEGORIES.find(c => c.k === t.category);
          return (
            <div
              key={i}
              style={{ padding: 10, border: '1px solid var(--border)', borderRadius: 12, marginBottom: 8, cursor: 'pointer' }}
              onClick={() => useTemplate(t)}
            >
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 2 }}>
                {cat?.icon} {cat?.label} · 优先级 {t.priority}
              </div>
              <div style={{ fontSize: 14 }}>{t.content}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function priorityColor(p: number): string {
  if (p >= 8) return '#ef4444';
  if (p >= 5) return '#f59e0b';
  return '#6b7280';
}

function niceError(e: unknown): string {
  if (e instanceof ApiCallError) return e.message;
  return (e as Error)?.message || '出错了';
}
